import { useCallback, useState } from "react";
import cx from "classnames";
import { faTag } from "@fortawesome/pro-regular-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";

import { ClientCartDetail, ICartItem } from "common/types/carts";
import { toPercent } from "common/utils/formatting";
import { toHumanizedList } from "common/utils/strings";
import { SubscriptionFrequency } from "common/types";
import { DeliveryMethodKind } from "common/types/fulfillments";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { deepCombineErrors } from "common/utils/errors";

import QuantityInput from "client/common/components/inputs/QuantityInput";
import Accordion from "client/common/components/interactive/Accordion";
import { useClinic, useFeatures } from "client/clinics/hooks";
import { Clinic } from "client/clinics/types";
import TextChip from "client/common/components/content/TextChip";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import Button from "client/common/components/buttons/Button";
import ProductLineSelections from "client/products/components/ProductLineSelections";
import { urls } from "client/routes";
import {
  getCartItemPricing,
  sendAddToCart,
  sendRemoveFromCart,
  sendSelectItem,
  useSendPostHogAddToCart,
  useSendPostHogItemClick,
  useSendPostHogRemoveFromCart,
} from "client/analytics/utils";
import { ItemClickSource } from "client/analytics/types";
import Alert from "client/common/components/content/Alert";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import {
  useCartItemDeleteMutation,
  useCartItemSubscriptionDeleteMutation,
  useCartItemSubscriptionCreateUpdateMutation,
  useCartItemUpdateMutationV2,
  useUpdateCartDeliveryMutation,
} from "../hooks";
import CartItemPrescriptions from "./CartItemPrescriptions";
import { getCartItemUnitPrice } from "../utils";

import styles from "../styles/CartItem.module.less";

type CartItemProps = {
  cartItem: ICartItem;
  cart: ClientCartDetail;
};

type CartItemFulfillmentFormProps = {
  mutation: ReturnType<typeof useUpdateCartDeliveryMutation>;
  cartItem: ICartItem;
  cart: ClientCartDetail;
};

const getOneTimePurchaseDescription = (cart: ClientCartDetail, clinic: Clinic) => {
  if (
    cart.is_eligible_for_first_autoship_discount &&
    cart.is_eligible_for_recurring_autoship_discount
  ) {
    return `Enable Autoship to save ${toPercent(
      clinic.first_subscription_discount,
    )} on your first Autoship and ${toPercent(
      clinic.recurring_subscription_discount,
    )} on all recurring orders`;
  }
  if (cart.is_eligible_for_first_autoship_discount) {
    return `Enable Autoship to save ${toPercent(
      clinic.first_subscription_discount,
    )} on your first Autoship`;
  }
  if (cart.is_eligible_for_recurring_autoship_discount) {
    return `Enable Autoship to save ${toPercent(
      clinic.recurring_subscription_discount,
    )} on all recurring orders`;
  }
  return "";
};

const CartItemFulfillmentForm = ({ mutation, cartItem, cart }: CartItemFulfillmentFormProps) => {
  const { mutate: updateFulfillment, error } = mutation;

  const selectPickup = (isPickup: boolean) => {
    const updates = [
      {
        cart_item_id: cartItem.id,
        is_pickup: isPickup,
      },
    ];
    const newCartItems = cart.items.map((ci) =>
      ci.id === cartItem.id ? { ...ci, is_pickup: isPickup } : ci,
    );

    updateFulfillment({ body: updates, optimisticData: { newCartItems } });
  };

  return (
    <div>
      {error && (
        <div className={styles.qtyError}>
          <Alert variant="danger">{deepCombineErrors(error.errors.non_field_errors)}</Alert>
        </div>
      )}

      <ProductLineSelections.DeliveryMethodSelectPanel
        fulfillmentOptions={cartItem.fulfillment_options}
        value={cartItem.is_pickup ? DeliveryMethodKind.Pickup : DeliveryMethodKind.Shipping}
        onChange={(val) => selectPickup(val === DeliveryMethodKind.Pickup)}
      />
    </div>
  );
};

type CartItemSubscriptionFormProps = {
  mutations: {
    createUpdate: ReturnType<typeof useCartItemSubscriptionCreateUpdateMutation>;
    delete: ReturnType<typeof useCartItemSubscriptionDeleteMutation>;
  };
  cartItem: ICartItem;
};

const CartItemSubscriptionForm = ({ mutations, cartItem }: CartItemSubscriptionFormProps) => {
  const { mutate: createUpdateSubscription } = mutations.createUpdate;
  const { mutate: deleteSubscription } = mutations.delete;

  const selectSubscription = (frequency: SubscriptionFrequency | null) => {
    if (!frequency) {
      deleteSubscription();
    } else {
      createUpdateSubscription(frequency);
    }
  };

  return (
    <div className={styles.form}>
      <ProductLineSelections.SubscriptionSelectPanel
        item={{
          ...cartItem,
          recommended_frequency: cartItem.recommended_frequency,
          eligible_for_autoship_discounts: cartItem.item.eligible_for_autoship_discounts,
        }}
        value={
          cartItem.subscription
            ? { ...cartItem.subscription, display: cartItem.subscription.frequency_display }
            : null
        }
        onChange={selectSubscription}
        quantity={cartItem.quantity}
      />
    </div>
  );
};

const CartItemSelections = ({ cartItem, cart }: CartItemProps) => {
  const clinic = useClinic();
  const updateCartDeliveryMutation = useUpdateCartDeliveryMutation(cart.id);

  const subscriptionMutations = {
    createUpdate: useCartItemSubscriptionCreateUpdateMutation(cart.id, cartItem.id),
    delete: useCartItemSubscriptionDeleteMutation(cart.id, cartItem.id),
  };

  const isSubscriptionLoading = !!Object.values(subscriptionMutations).find(
    (mutation) => mutation.isLoading,
  );

  const items = [
    {
      key: "fulfillment",
      ...(cartItem.is_pickup
        ? {
            label: "Pickup from clinic",
            description: "We will text you instructions for pickup after placing your order",
          }
        : {
            label: "Ship to home",
            description: "Select a shipping speed during checkout",
          }),
      content: (
        <CartItemFulfillmentForm
          cartItem={cartItem}
          mutation={updateCartDeliveryMutation}
          cart={cart}
        />
      ),
      isLoading: updateCartDeliveryMutation.isLoading,
      isVisible: cartItem.fulfillment_options.length > 1,
    },
    {
      key: "autoship",
      ...(cartItem.subscription
        ? {
            label: `Autoship enabled`,
            description: cartItem.subscription.frequency_display,
          }
        : {
            label: "One-time purchase",
            description: getOneTimePurchaseDescription(cart, clinic),
          }),
      content: <CartItemSubscriptionForm cartItem={cartItem} mutations={subscriptionMutations} />,
      isLoading: isSubscriptionLoading,
      isVisible: cartItem.is_subscribable,
    },
    {
      key: "prescription",
      ...(cartItem.available_prescriptions.length
        ? {
            label: `Prescription on file for ${toHumanizedList(
              cartItem.available_prescriptions.map(
                (prescription) => prescription.patient.name_display,
              ),
            )}`,
            description: "You're preauthorized to purchase this medication",
          }
        : {
            label: "Prescription required",
            description: `Approval from ${cartItem.primary_doctor.name_display} will be requested on checkout`,
          }),
      content: <CartItemPrescriptions cartItem={cartItem} />,
      isVisible: cartItem.item.product.product_line.prescription_required,
    },
  ].filter(({ isVisible }) => isVisible);

  return (
    <div className={styles.selections}>
      <Accordion.Single
        classNames={{
          header: styles.header,
          item: styles.item,
          description: styles.long,
          icon: styles.icon,
          content: styles.content,
        }}
        initialOpenKey=""
        items={items}
      />
    </div>
  );
};

type CartItemDeleteProps = CartItemProps & { onDeleteFinish: () => void; isDeleting: boolean };

const CartItemDelete = ({ cartItem, cart, onDeleteFinish, isDeleting }: CartItemDeleteProps) => {
  const { mutate: deleteItem, isLoading: isDeleteLoading } = useCartItemDeleteMutation(cart.id, {
    onSuccess: onDeleteFinish,
  });

  return (
    <div className={cx(styles.delete, isDeleting && styles.deleting)}>
      <p className={styles.title}>Are you sure you want to remove this item?</p>
      <div className={styles.miniItem}>
        <CatalogImage
          className={styles.miniImage}
          image={cartItem.item.image}
          size="thumbnail"
          alt={cartItem.item.name}
        />
        <div className={styles.names}>
          <p className={styles.name}>{cartItem.item.product.product_line.name}</p>
          <p className={styles.description}>{cartItem.item.product_line_relative_name}</p>
        </div>
      </div>
      <div className={styles.actions}>
        <button className={styles.keep} type="button" onClick={onDeleteFinish}>
          No, keep item
        </button>
        <Button size="s" onClick={() => deleteItem(cartItem.id)} isLoading={isDeleteLoading}>
          Yes, remove item
        </Button>
      </div>
    </div>
  );
};

type CartItemMainProps = CartItemProps & { onDelete: () => void };

const CartItemMain = ({ cartItem, cart, onDelete }: CartItemMainProps) => {
  const features = useFeatures();
  const { close } = useCurrentDrawer();
  const sendPostHogAddToCart = useSendPostHogAddToCart();
  const sendPostHogItemClick = useSendPostHogItemClick();
  const sendPostHogRemoveFromCart = useSendPostHogRemoveFromCart();
  const {
    mutate: updateItem,
    isLoading,
    error,
  } = useCartItemUpdateMutationV2(cartItem.id, cart.id);

  const itemUrl = urls.products.detail({
    params: { id: cartItem.item.product.product_line.id },
    search: {
      item: cartItem.item.id,
      product: cartItem.item.product.id,
    },
  });

  const handleItemClick = () => {
    const unitPrice = getCartItemUnitPrice(cartItem);
    sendSelectItem(cartItem.item, ItemClickSource.CartItem, unitPrice);
    sendPostHogItemClick(cartItem.item, {
      source: ItemClickSource.CartItem,
      unit_price: unitPrice,
    });
    close();
  };

  // useCallback to enable debounce in the quantity input
  const updateQuantity = useCallback(
    (quantity: number) => {
      const { price, discount } = getCartItemPricing(cartItem);
      if (quantity > cartItem.quantity) {
        const quantityAdded = quantity - cartItem.quantity;
        sendAddToCart({
          item: cartItem.item,
          quantity: quantityAdded,
          price,
          discount,
        });
        sendPostHogAddToCart({
          cart_id: cart.id,
          cart_item_id: cartItem.id,
          item_id: cartItem.item.id,
          product_id: cartItem.item.product.id,
          product_line_id: cartItem.item.product.product_line.id,
          patient_id: cartItem.patient?.id ?? null,
          flavor_selection_id: cartItem.flavor_selection ?? null,
          quantity: quantityAdded,
          unit_price: price,
          discount,
        });
      }
      if (quantity < cartItem.quantity) {
        const quantityRemoved = cartItem.quantity - quantity;
        sendRemoveFromCart({
          item: cartItem.item,
          quantity: quantityRemoved,
          price,
          discount,
        });
        sendPostHogRemoveFromCart({
          cart_id: cart.id,
          cart_item_id: cartItem.id,
          item_id: cartItem.item.id,
          patient_id: cartItem.patient?.id ?? null,
          flavor_selection_id: cartItem.flavor_selection ?? null,
          quantity: quantityRemoved,
          unit_price: price,
          discount,
        });
      }
      updateItem({ body: { quantity } });
    },
    [updateItem, cartItem, cart.id, sendPostHogAddToCart, sendPostHogRemoveFromCart],
  );

  return (
    <div className={styles.main}>
      <CatalogImage.Link
        className={styles.image}
        alt={cartItem.item.name}
        image={cartItem.item.image}
        size="medium"
        dest={itemUrl}
        onClick={handleItemClick}
      />
      <div className={styles.info}>
        <div className={styles.primary}>
          <div className={styles.names}>
            <LinkButton dest={itemUrl} className={styles.name} onClick={handleItemClick}>
              {cartItem.item.product.product_line.name}
            </LinkButton>
            <p className={styles.description}>
              {cartItem.item.product_line_relative_name}
              {cartItem.flavors_display && ` · ${cartItem.flavors_display}`}
              {cartItem.patient && ` · ${cartItem.patient.name_display}`}
            </p>
          </div>
          {features.PROMOTION_DISCOUNT_BREAKDOWN
            ? !!cartItem.discount_message_details?.length && (
                <div className={styles.tags}>
                  {cartItem.discount_message_details.map((message) => (
                    <TextChip variant="promotion" key={message.display_text} isMultiline={true}>
                      {message.promo_code && <Icon icon={faTag} />}
                      {message.display_text}
                    </TextChip>
                  ))}
                </div>
              )
            : !!cartItem.discount_messages.length && (
                <div className={styles.tags}>
                  {cartItem.discount_messages.map((message) => (
                    <TextChip variant="promotion" key={message} isMultiline={true}>
                      {message}
                    </TextChip>
                  ))}
                </div>
              )}
          {cartItem.item_warning && <Alert variant="warning">{cartItem.item_warning}</Alert>}
        </div>
        <div className={styles.more}>
          <div className={styles.quantity}>
            <QuantityInput.Debounce
              className={styles.input}
              min={1}
              value={cartItem.quantity}
              onChange={updateQuantity}
              onRemove={onDelete}
            />
          </div>
          {isLoading ? (
            <Spinner />
          ) : (
            <CrossedPrice
              classNames={{
                container: styles.crossedPrice,
                price: styles.price,
                crossed: styles.crossed,
              }}
              crossed={cartItem.subtotal}
              price={cartItem.discounted_subtotal}
            />
          )}
        </div>
        {error && <Alert variant="danger">{deepCombineErrors(error.errors)}</Alert>}
      </div>
      <button onClick={onDelete} type="button" className={styles.deleteIcon}>
        <Icon icon={faTimes} />
      </button>
    </div>
  );
};

const CartItem = ({ cartItem, cart }: CartItemProps) => {
  const [isDeleting, setIsDeleting] = useState(false);

  return (
    <div className={styles.container}>
      <CartItemDelete
        cart={cart}
        cartItem={cartItem}
        isDeleting={isDeleting}
        onDeleteFinish={() => setIsDeleting(false)}
      />
      <CartItemMain cart={cart} cartItem={cartItem} onDelete={() => setIsDeleting(true)} />
      <CartItemSelections cart={cart} cartItem={cartItem} />
    </div>
  );
};

export default CartItem;
